/**
 * 图谱头部组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useGraphStore, type RenderMode } from '../../store/graphStore'
import { X, RefreshCw, Loader2 } from 'lucide-react'

interface GraphHeaderProps {
  onClose?: () => void  // 可选，页面模式下不需要关闭按钮
  onRefresh: () => void
  title?: string  // 可选，自定义标题
}

export default function GraphHeader({ onClose, onRefresh, title }: GraphHeaderProps) {
  const { 
    actualMode, 
    setRenderMode, 
    isLoading,
    performanceLevel,
    getProgress,
  } = useGraphStore()
  
  const progress = getProgress()
  
  const handleModeChange = (mode: RenderMode) => {
    setRenderMode(mode)
  }
  
  return (
    <div className="flex items-center justify-between p-4 border-b border-border">
      {/* 左侧：标题和统计 */}
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
          🕸️ {title || '知识图谱'}
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
        </h2>
        <p className="text-sm text-slate-400">
          实体: {progress.loaded > 0 ? `${progress.loaded}` : '0'} 
          {progress.total > progress.loaded && ` / ${progress.total}`}
          <span className="mx-2">|</span>
          模式: {actualMode.toUpperCase()}
          {performanceLevel === 'low' && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              省电模式
            </span>
          )}
        </p>
      </div>
      
      {/* 右侧：控制按钮 */}
      <div className="flex items-center gap-2">
        {/* 模式切换 */}
        <div className="flex rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
          {(['2d', '3d'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => handleModeChange(mode)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                actualMode === mode 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-transparent text-slate-400'
              }`}
            >
              {mode.toUpperCase()}
            </button>
          ))}
        </div>
        
        {/* 刷新按钮 */}
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="p-2 rounded-lg transition-colors hover:opacity-80 text-slate-400 disabled:opacity-50"
          title="刷新数据"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
        
        {/* 关闭按钮（仅在弹窗模式下显示） */}
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:opacity-80 text-slate-400"
            title="关闭"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  )
}

